import { Divider, Typography } from '@mui/material';
import FormField from 'further-ui/components/FormField';
import PageLoader from 'components/PageLoader';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  ConsiderationType,
  ConsiderationTypeHoldingClassTypes,
  PartialRealisationAllocationLogic,
} from 'further-types/lp/realisation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import api from 'lib/trpcClient.ts';
import React from 'react';
import { useParams } from 'react-router-dom';
import CashConsiderationsTable from '../CreateRealisation/Summary/CashConsiderationsTable';
import ConsiderationOverview from '../CreateRealisation/Summary/ConsiderationOverview.tsx/index.tsx';
import getRealisationType from '../CreateRealisation/Summary/transactions/helpers/getRealisationType.ts';
import Page from 'components/new/Page/index.tsx';
import { LP_REALISATION_LIST } from 'adminConstants/routes.ts';
import AvatarWithName from 'components/new/AvatarWithName/index.tsx';
import Column from 'further-ui/components/Layout/Column.tsx';

type HoldingClassRealisationAllocation = {
  holdingClassId: string;
  name: string;
  partialRealisationAllocationLogic: PartialRealisationAllocationLogic;
  unitsSold: number;
  unitsAllocationPerInvestment: Array<{
    investmentId: string;
    investmentCurrency: CurrencyCode;
    allocatedUnits: number;
    receivedConsiderationTotalTcy: number;
    escrowConsiderationTotalTcy: number;
    earnoutConsiderationTotalTcy: number;
    withholdingTaxPayableTcy: number;
    receivedConsiderationTotalLcy: number;
    escrowConsiderationTotalLcy: number;
    earnoutConsiderationTotalLcy: number;
    withholdingTaxPayableLcy: number;
  }>;
};

type ConsiderationEntry = {
  perHoldingClassConsiderationAmounts: Array<{
    holdingClassId: string;
    receivedConsiderationPerUnitTcy: number;
    receivedConsiderationTotalTcy: number;
    escrowConsiderationTotalTcy: number;
    earnoutConsiderationTotalTcy: number;
    withholdingTaxPayableTcy: number;
  }>;
};

const formatHoldingClassRealisation = (
  allocation: HoldingClassRealisationAllocation,
  cashConsiderationEntry?: ConsiderationEntry,
) => {
  if (!cashConsiderationEntry) {
    throw new Error('Cash consideration entry not found');
  }

  const considerationAmounts =
    cashConsiderationEntry?.perHoldingClassConsiderationAmounts.find(
      (amount) => amount.holdingClassId === allocation.holdingClassId,
    );

  return {
    holdingClassId: allocation.holdingClassId,
    name: allocation.name,
    partialRealisationAllocationLogic:
      allocation.partialRealisationAllocationLogic,
    unitsSold: allocation.unitsSold,
    cashConsiderationAmounts: {
      receivedConsiderationPerUnit:
        considerationAmounts?.receivedConsiderationPerUnitTcy ?? 0,
      receivedConsiderationTotal:
        considerationAmounts?.receivedConsiderationTotalTcy ?? 0,
      escrowConsiderationTotal:
        considerationAmounts?.escrowConsiderationTotalTcy ?? 0,
      earnoutConsiderationTotal:
        considerationAmounts?.earnoutConsiderationTotalTcy ?? 0,
      withholdingTaxPayable:
        considerationAmounts?.withholdingTaxPayableTcy ?? 0,
    },
  };
};

const ViewRealisation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { selectedVehicleId, vehicle } = useLayoutContext();

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const [realisation] = api.lpRealisation.get.useSuspenseQuery({
    realisationId: id ?? '',
    vehicleId: selectedVehicleId ?? '',
  });

  if (!vehicle) {
    return <PageLoader />;
  }

  const cashConsiderationEntry = realisation.considerationEntries.find(
    (entry) =>
      entry.considerationType === ConsiderationType.CashAndWithholdingTax,
  );

  const considerationCurrency =
    cashConsiderationEntry?.currencyCode ?? vehicle?.baseCurrency;

  const holdingClassCurrencies =
    realisation.holdingClassRealisationAllocations.flatMap((holdingClass) =>
      holdingClass.unitsAllocationPerInvestment?.map(
        (investment) => investment.investmentCurrency,
      ),
    );

  const holdingClassCurrency =
    holdingClassCurrencies[0] ?? vehicle?.baseCurrency;

  const realisationType = getRealisationType({
    holdingClassInvestment: {
      currency: holdingClassCurrency ?? considerationCurrency,
    },
    vehicleCurrencyCode: vehicle?.baseCurrency,
    realisationCurrencyCode: realisation.currencyCode,
  });

  const currencyForTcyDisplay =
    realisationType ===
    ConsiderationTypeHoldingClassTypes.LcyConsiderationForNonLcyHoldingClass
      ? holdingClassCurrency ?? considerationCurrency
      : considerationCurrency;

  const forceShowTcy =
    currencyForTcyDisplay !== realisation.currencyCode &&
    realisation.currencyCode !== vehicle.baseCurrency;

  const formattedHoldingClassRealisations =
    realisation.holdingClassRealisationAllocations.map((allocation) =>
      formatHoldingClassRealisation(allocation, cashConsiderationEntry),
    );

  const investorAllocationsTotals =
    cashConsiderationEntry?.investorAllocations?.map((allocation) => ({
      commitmentId: allocation.commitmentId,
      investorName: allocation.investorLabel,
      receivedConsideration: allocation.receivedConsiderationTcy,
      escrowConsideration: allocation.escrowConsiderationTcy,
      earnoutConsideration: allocation.earnoutConsiderationTcy,
      withholdingTaxPayable: allocation.withholdingTaxPayableTcy,
      averageAllocationPercentage: allocation.allocationPercentage,
      total:
        allocation.receivedConsiderationTcy +
        allocation.escrowConsiderationTcy +
        allocation.earnoutConsiderationTcy,
    }));

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  const title = `${
    realisation.assetName
  }, ${realisation.ledgerDate.toReadable()}`;

  return (
    <Page
      tabTitle={`Realisations: ${title}`}
      withStickyHeader
      heading={<AvatarWithName name={title} />}
      goBackAction={LP_REALISATION_LIST}
      withHeadingDivider
      spacing="lg"
    >
      <FormField labelColorLight label="Vehicle">
        <Typography variant="h6">{realisation.vehicleName}</Typography>
      </FormField>
      <FormField labelColorLight label="Realised asset">
        <Typography variant="h6">{realisation.assetName}</Typography>
      </FormField>
      <FormField labelColorLight label="Realisation date">
        <Typography variant="h6">
          {realisation.ledgerDate.toReadable()}
        </Typography>
      </FormField>
      <FormField labelColorLight label="Classes realised">
        <Typography variant="h6">
          {realisation.holdingClassRealisationAllocations
            .map((allocation) => allocation.name)
            .join(', ')}
        </Typography>
      </FormField>
      <FormField labelColorLight label="Total units realised">
        <Typography variant="h6">
          {numberToDisplayString(realisation.numberOfUnitsRealised)}
        </Typography>
      </FormField>
      <FormField labelColorLight label="Total realisation value">
        <Typography variant="h6">
          {numberToCurrencyString(realisation.totalRealisationAmountLcy, {
            currency: vehicle.baseCurrency ?? realisation.currencyCode,
          })}
        </Typography>
      </FormField>
      <CashConsiderationsTable
        currency={realisation.currencyCode}
        holdingClassRealisations={formattedHoldingClassRealisations}
      />
      <Divider />
      <ConsiderationOverview
        currency={realisation.currencyCode}
        holdingClassRealisations={formattedHoldingClassRealisations}
        investorAllocationsTotals={investorAllocationsTotals ?? []}
      />
      <Divider />
      <Column spacing="sm" fullWidth>
        <Typography variant="h5">Account transactions</Typography>
        {vehicle && (
          <TransactionAllocator
            transactionTypeOptions={transactionTypeOptions}
            transactions={realisation.transactions ?? []}
            transactionCurrency={currencyForTcyDisplay}
            fxRate={realisation.currencyRate ?? null}
            vehicle={vehicle}
            forceShowTcy={forceShowTcy}
            readOnly={true}
            showCurrencyRate={true}
          />
        )}
      </Column>
    </Page>
  );
};

export default ViewRealisation;
